import React, { useContext, useEffect, useMemo } from "react";
import TimelinePage from "features/trades/TradeDrawer/Timeline/TimelinePage";
import {
  BACKTESTING_EXTRA_DETAILS_PATH,
  SMALL_SCREEN,
  TRADE_EXTRA_DETAILS_PATH,
} from "common/constants";
import { useRouteMatch } from "react-router-dom";
import OrdersPage from "features/trades/TradeDrawer/Orders/OrdersPage";
import ExtraDetailsDrawer from "common/ExtraDetails/ExtraDetailsDrawer";
import TradeDrawerTitle from "features/trades/TradeDrawer/TradeDrawerTitle";
import { getRouteWithoutPath, getTradeExtraDetailsRoute } from "utils/routes";
import EditTradeContext from "@shared/features/trades/EditTrade/EditTrade.context";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import EditTradeWrapper from "@shared/features/trades/EditTrade/EditTradeWrapper";
import { TradeDetailsContext } from "@shared/features/trades/TradeDetails.context";
import TradeDetailsProvider from "@shared/features/trades/TradeDetailsProvider";
import GridsTab from "features/trades/TradeDrawer/Grids/GridsTab";
import { QueryStatus } from "@shared/types";
import useMedia from "utils/hooks/useMedia";
import TradeChart from "features/trades/TradeDrawer/TradeChart";
import { useBottomNavigator, useDrawerDelayedUnmount } from "utils/hooks/misc";
import {
  confirmWithCallback,
  displayResultMessage,
  openMessage,
  showUnsavedChangesWarning,
} from "utils/misc";
import { ValueOf } from "@shared/types/utils";
import Modal from "common/Modal/Modal";
import useGeneralConfiguration from "features/configuration/GeneralConfiguration/useGeneralConfiguration";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import GeneralTab from "common/ExtraDetails/GeneralTab.tsx";
import NewLookTradeGeneralTab from "features/trades/TradeDrawer/NewLookTradeGeneralTab.tsx";
import { useTheme } from "styled-components";
import Text from "common/Typography/Text.tsx";
import { selectTradeExtraInfoGeneralData } from "app/slices/trades.slice.ts";
import { useSelector } from "react-redux";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import {
  TRADE_EXTRA_DETAILS_TABS,
  TRADE_EXTRA_DETAILS_TABS_TO_DISPLAY,
} from "@shared/common/constants.ts";
import { useDisplayTradeDetails } from "@shared/utils/hooks/misc.ts";

const TradeDrawer = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const match = useRouteMatch(TRADE_EXTRA_DETAILS_PATH);
  const { historyPush } = useHistoryWithQuery();
  const isBacktesting = !!useRouteMatch(BACKTESTING_EXTRA_DETAILS_PATH);
  const [confirmModal, confirmModalContextHolder] = Modal.useModal();
  const displayTradeDetails = useDisplayTradeDetails();

  const {
    tradeId: tradeIdStr,
    tab,
    edit,
  } = (match?.params || {}) as {
    tradeId: string;
    tab: ValueOf<typeof TRADE_EXTRA_DETAILS_TABS>;
    edit: string;
  };

  const tradeId = displayTradeDetails ? parseInt(tradeIdStr) : undefined;
  const { id: drawerTradeId } = useDrawerDelayedUnmount(tradeId);
  const isEdit = edit === "edit";

  const exitEditMode = (response?: { data: number | {} }) => {
    const route = getRouteWithoutPath(
      location.pathname,
      TRADE_EXTRA_DETAILS_PATH
    );
    const tradeId =
      response && typeof response.data === "number"
        ? response.data
        : drawerTradeId;
    if (tradeId) {
      historyPush(getTradeExtraDetailsRoute(route, tradeId, tab, false));
    }
  };

  const showConfirmEditWarning = () => {
    return confirmWithCallback(
      confirmModal,
      <>
        <Text>{t("confirm_edit.automated_signal_message")}</Text>
        <Text style={{ marginTop: 4 }}>
          {t("confirm_edit.continue_question")}
        </Text>
      </>,
      t("confirm_edit.title"),
      true,
      t("confirm_edit.yes"),
      t("confirm_edit.no")
    );
  };

  return (
    <TradeDetailsProvider
      tradeId={drawerTradeId}
      openMessage={openMessage}
      isBacktesting={isBacktesting}
      isEditTrade={isEdit}
    >
      <EditTradeWrapper
        isEdit={isEdit}
        tab={tab}
        tradeId={drawerTradeId}
        confirmModalContextHolder={confirmModalContextHolder}
        confirmWithCallback={(message: string) =>
          confirmWithCallback(confirmModal, message)
        }
        openMessage={openMessage}
        displayResultMessage={displayResultMessage}
        useGeneralConfiguration={useGeneralConfiguration}
        showUnsavedChangesWarning={() =>
          showUnsavedChangesWarning(confirmModal)
        }
        showConfirmEditWarning={showConfirmEditWarning}
        exitEditMode={exitEditMode}
      >
        <TradeDrawerContent
          tradeId={drawerTradeId}
          tab={tab}
          isOpen={!!tradeId}
        />
      </EditTradeWrapper>
    </TradeDetailsProvider>
  );
};

export default TradeDrawer;

interface TradeDrawerContentProps {
  tradeId?: number;
  isOpen?: boolean;
  tab: ValueOf<typeof TRADE_EXTRA_DETAILS_TABS>;
}

const TradeDrawerContent = ({
  tradeId,
  tab,
  isOpen,
}: TradeDrawerContentProps) => {
  const {
    baseArgs,
    generalData,
    isGridBotTrade,
    tradeMetadata,
    getTradeExtraInfoOrdersStatus,
    getTradeExtraInfoGeneralStatus,
    getTradeExtraInfoConfigurationStatus,
    getTradeExtraInfoStatsStatus,
    getTradeExtraInfoActivitiesStatus,
    getTradeExtraInfoGridsStatus,
    configurationData,
  } = useContext(TradeDetailsContext)!;
  const {
    onCancelChangesClick,
    generalConfiguration,
    editTradeConfigurationLoading,
    isEdit,
  } = useContext(EditTradeContext) || {};
  const isSmallScreen = useMedia(
    [`(max-width: ${SMALL_SCREEN})`],
    [true],
    false
  );
  const { historyPush } = useHistoryWithQuery();
  const defaultTab = TRADE_EXTRA_DETAILS_TABS.GENERAL;
  const drawerOpen = !!tradeId;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const generalInfoSelector = useMemo(
    () => selectTradeExtraInfoGeneralData(baseArgs),
    [baseArgs]
  );
  const generalInfoData = useSelector(generalInfoSelector);
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const { isNewLook } = useTheme();

  const onTabChange = (tab: string) => {
    const baseRoute = getRouteWithoutPath(
      location.pathname,
      TRADE_EXTRA_DETAILS_PATH
    );
    if (tradeId) {
      historyPush(getTradeExtraDetailsRoute(baseRoute, tradeId, tab, isEdit));
    }
  };

  useEffect(() => {
    if (drawerOpen && !Object.values(TRADE_EXTRA_DETAILS_TABS).includes(tab)) {
      onTabChange(defaultTab);
    }
  }, [drawerOpen]);

  const commonOrdersQueries = [
    getTradeExtraInfoOrdersStatus,
    getTradeExtraInfoActivitiesStatus,
  ];
  const commonGeneralQueries = [
    getTradeExtraInfoGeneralStatus,
    getTradeExtraInfoStatsStatus,
  ];

  const tabToQueries: Record<string, QueryStatus[]> = {
    [TRADE_EXTRA_DETAILS_TABS.GENERAL]: commonGeneralQueries,
    [TRADE_EXTRA_DETAILS_TABS.TIMELINE]: commonOrdersQueries,
    [TRADE_EXTRA_DETAILS_TABS.GRIDS]: [
      ...commonOrdersQueries,
      getTradeExtraInfoGridsStatus,
    ],
    [TRADE_EXTRA_DETAILS_TABS.ORDERS]: [
      ...commonOrdersQueries,
      getTradeExtraInfoStatsStatus,
    ],
    [TRADE_EXTRA_DETAILS_TABS.CONFIGURATION]: [
      getTradeExtraInfoConfigurationStatus,
    ],
  };

  const isLoading = (tab: string) =>
    tabToQueries[tab].some((query) => query.isLoading);

  const loadingConfiguration =
    isLoading(TRADE_EXTRA_DETAILS_TABS.CONFIGURATION) ||
    editTradeConfigurationLoading ||
    !configurationData;

  const ordersTab = isGridBotTrade ? <GridsTab /> : <OrdersPage />;
  const tabToComponent = {
    [TRADE_EXTRA_DETAILS_TABS.GENERAL]: isNewLook ? (
      <NewLookTradeGeneralTab data={generalInfoData} />
    ) : (
      <GeneralTab data={generalInfoData} />
    ),
    [TRADE_EXTRA_DETAILS_TABS.TIMELINE]: <TimelinePage />,
    [isGridBotTrade
      ? TRADE_EXTRA_DETAILS_TABS.GRIDS
      : TRADE_EXTRA_DETAILS_TABS.ORDERS]: ordersTab,
    [TRADE_EXTRA_DETAILS_TABS.CONFIGURATION]: (
      <LoadingSpinnerOverlay
        isLoading={loadingConfiguration}
        hideContentWhileLoading={!generalConfiguration}
        spinnerSize="large"
      >
        {generalConfiguration}
      </LoadingSpinnerOverlay>
    ),
  };

  const getTabLoadingData = (tabKey: string) => {
    let isLoading = tabToQueries[tabKey].some((query) => query.isLoading);
    if (tabKey === TRADE_EXTRA_DETAILS_TABS.GENERAL) {
      isLoading = isLoading || !generalInfoData;
    } else if (tabKey === TRADE_EXTRA_DETAILS_TABS.CONFIGURATION) {
      isLoading = loadingConfiguration;
    }
    return {
      loading: isLoading,
      refreshing:
        !isLoading &&
        tabToQueries[tabKey].some(
          (query) => query.isFetching && !!query.currentData
        ),
    };
  };

  return (
    <ExtraDetailsDrawer
      path={TRADE_EXTRA_DETAILS_PATH}
      visible={!!isOpen}
      tabsToComponents={tabToComponent}
      title={
        tradeId ? (
          <TradeDrawerTitle
            tradeExtraInfoGeneral={generalData}
            tradeId={tradeId}
            tab={tab}
            isLoading={
              getTradeExtraInfoGeneralStatus.isLoading ||
              getTradeExtraInfoOrdersStatus.isLoading ||
              getTradeExtraInfoStatsStatus.isLoading
            }
          />
        ) : undefined
      }
      tab={tab}
      hideCloseButton={isEdit}
      hideTabs={isEdit || getTradeExtraInfoStatsStatus.isLoading}
      onTabChange={onTabChange}
      onCloseDrawer={onCancelChangesClick}
      extension={
        isSmallScreen || !tradeMetadata ? undefined : (
          <TradeChart isExtension={true} />
        )
      }
      innerContentWrapperStyle={{
        margin: 0,
        paddingTop: 24,
        paddingBottom: isBottomNavigator ? 50 : 0,
      }}
      tabsStyle={{ marginBottom: 0 }}
      getTabLoadingData={isEdit ? undefined : getTabLoadingData}
      tabToQueries={isEdit ? undefined : tabToQueries}
      getTabLabel={(tab) => t(TRADE_EXTRA_DETAILS_TABS_TO_DISPLAY[tab])}
    />
  );
};
